/**
 * 屏 14 — 兵主殿（Armory Hall）
 *
 * 导航枢纽：首页与二级功能页之间的中间层。
 * 4 张等大卡片横向排列 + Canvas 战术背景 + 右上角快捷入口。
 */

import { setTransitionTarget, type TransitionTarget } from "./screen-15-transition";

// ============================================================
// 过渡目标工厂（避免散落各处的 setTransitionTarget 调用）
// ============================================================

const TRANSITION_TARGETS: Record<string, TransitionTarget> = {
  forge: {
    title: "铸剑于阁",
    protocol: "FORGE.PROTOCOL // 0x01",
    accentColor: "var(--tactical-green)",
    targetHash: "#/forge",
    subtitle: "锻代码为剑，淬战术为锋",
  },
  sharpen: {
    title: "砺锋于台",
    protocol: "SHARPEN.PROTOCOL // 0x02",
    accentColor: "var(--tactical-cyan)",
    targetHash: "#/sharpen",
    subtitle: "承战颅遗志，启群智之涌",
  },
  bear: {
    title: "承刃而行",
    protocol: "BEAR.PROTOCOL // 0x03",
    accentColor: "var(--valorant-red)",
    targetHash: "#/levels",
    subtitle: "持剑入战，以战证道",
  },
  sandbox: {
    title: "沙盘演兵",
    protocol: "SANDBOX.PROTOCOL // 0x04",
    accentColor: "var(--text-secondary)",
    targetHash: "#/my-works",
    subtitle: "演兵于盘，谋定后动",
  },
  arena: {
    title: "斗蛐争锋",
    protocol: "ARENA.PROTOCOL // 0x05",
    accentColor: "var(--tactical-amber)",
    targetHash: "#/arena",
    subtitle: "算法竞逐，适者生存",
  },
  settings: {
    title: "系统调校",
    protocol: "CONFIG.PROTOCOL // 0x06",
    accentColor: "var(--text-secondary)",
    targetHash: "#/settings",
    subtitle: "参数校准，武器维护",
  },
  terminal: {
    title: "承刃终端",
    protocol: "TERMINAL.PROTOCOL // 0x00",
    accentColor: "var(--valorant-red)",
    targetHash: "#/",
    subtitle: "重返品牌之始",
  },
};

// ============================================================
// 卡片数据
// ============================================================

interface CardData {
  key: string;
  title: string;
  protocol: string;
  statLabel: string;
  statDetail: string;
}

const CARDS: CardData[] = [
  {
    key: "forge",
    title: "铸 剑 于 阁",
    protocol: "FORGE.PROTOCOL",
    statLabel: "学习进度",
    statDetail: "5 章",
  },
  {
    key: "sharpen",
    title: "砺 锋 于 台",
    protocol: "SHARPEN.PROTOCOL",
    statLabel: "实战试炼",
    statDetail: "即刻挑战",
  },
  {
    key: "bear",
    title: "承 刃 而 行",
    protocol: "BEAR.PROTOCOL",
    statLabel: "关卡挑战",
    statDetail: "持剑入战",
  },
  {
    key: "sandbox",
    title: "沙 盘 演 兵",
    protocol: "SANDBOX.PROTOCOL",
    statLabel: "我的作品",
    statDetail: "演兵推演",
  },
];

const CARD_COLORS: Record<string, string> = {
  forge: "var(--tactical-green)",
  sharpen: "var(--tactical-cyan)",
  bear: "var(--valorant-red)",
  sandbox: "var(--text-secondary)",
};

// ============================================================
// 主渲染函数
// ============================================================

export function renderScreen14ArmoryHall(
  app: HTMLElement,
  onNavigate: (path: string) => void
): void {
  // ---- 导航辅助：设过渡目标 → 跳转过渡页 ----
  function navigateWithTransition(key: string) {
    // 返回品牌页时清除访问标记，让首页重播完整开机动画
    if (key === "terminal") {
      localStorage.removeItem("battlecode-armory-visited");
    }
    const target = TRANSITION_TARGETS[key];
    if (target) {
      setTransitionTarget(target);
      onNavigate("#/transition");
    }
  }

  // ---- 卡片 HTML ----
  function buildCardHTML(card: CardData): string {
    const color = CARD_COLORS[card.key] || "var(--text-secondary)";
    const imgSrc = getCardIllustration(card.key);
    return `
      <div class="s14-card" data-target="${card.key}">
        <div class="s14-card-decor s14-decor-top">
          <span>◇</span><span>◇</span><span>◇</span><span>◇</span>
        </div>
        <div class="s14-card-illustration">
          <img src="${imgSrc}" alt="${card.title}" class="s14-card-img" style="--card-accent: ${color}" />
        </div>
        <div class="s14-card-title" style="color: ${color}">
          ${card.title}
        </div>
        <div class="s14-card-protocol">
          ${card.protocol}
        </div>
        <div class="s14-card-stats">
          <span class="s14-card-stat-label">${card.statLabel}</span>
          <span class="s14-card-stat-detail" style="color: ${color}">${card.statDetail}</span>
        </div>
        <div class="s14-card-decor s14-decor-bot">
          <span>◇</span><span>◇</span><span>◇</span><span>◇</span>
        </div>
      </div>
    `;
  }

  // ---- DOM 结构 ----
  app.innerHTML = `
    <div class="s14-armory-hall">
      <canvas id="s14-bg-canvas"></canvas>

      <!-- 顶部状态栏 -->
      <div class="s14-top-bar">
        <button class="s14-brand-return" data-transition="terminal">
          ◈ ◄ 承刃终端
        </button>
        <div class="s14-top-right">
          <button class="s14-link" data-transition="arena">斗蛐争锋</button>
          <span class="s14-link-sep">|</span>
          <button class="s14-link" data-transition="settings">系统调校</button>
        </div>
      </div>

      <!-- 卡片行 -->
      <div class="s14-cards-row">
        ${CARDS.map(buildCardHTML).join("")}
      </div>

      <!-- 底部状态栏 -->
      <div class="s14-status-bar">
        <span>// ARMORY HALL //</span>
        <span class="s14-status-version">v11</span>
      </div>

      <!-- HUD 四角括号 -->
      <div class="s14-hud-corners">
        <div class="hud-corner tl"></div>
        <div class="hud-corner tr"></div>
        <div class="hud-corner bl"></div>
        <div class="hud-corner br"></div>
      </div>
    </div>
  `;

  // ---- 事件绑定 ----
  // 卡片点击
  app.querySelectorAll(".s14-card").forEach((el) => {
    el.addEventListener("click", () => {
      const target = (el as HTMLElement).dataset.target;
      if (target) navigateWithTransition(target);
    });
  });

  // 顶部链接（右上角 + 左上角）
  app.querySelectorAll("[data-transition]").forEach((el) => {
    el.addEventListener("click", () => {
      const key = (el as HTMLElement).dataset.transition;
      if (key) navigateWithTransition(key);
    });
  });

  // ---- Canvas 背景 ----
  const canvas = app.querySelector("#s14-bg-canvas") as HTMLCanvasElement;
  let canvasCleanup: (() => void) | null = null;

  if (canvas) {
    canvasCleanup = initArmoryBackground(canvas);
  }

  // ---- Cleanup ----
  (app as any)._s14Cleanup = () => {
    canvasCleanup?.();
  };
}

// ============================================================
// Canvas 背景：战术网格 + 锻炉辉光 + 台座平台线 + vignette
// ============================================================

function initArmoryBackground(canvas: HTMLCanvasElement): () => void {
  let rafId = 0;
  let running = true;

  function draw(ctx: CanvasRenderingContext2D, w: number, h: number) {
    // 纯暗底色
    ctx.fillStyle = "#0F1923";
    ctx.fillRect(0, 0, w, h);

    // 45° 战术网格（比首页更淡更稀疏）
    ctx.save();
    ctx.strokeStyle = "rgba(0, 228, 255, 0.04)";
    ctx.lineWidth = 0.5;
    const spacing = 40;
    const diag = Math.ceil((w + h) / spacing);
    for (let i = -diag; i < diag; i++) {
      const offset = i * spacing;
      ctx.beginPath();
      ctx.moveTo(offset, 0);
      ctx.lineTo(offset - h, h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(offset, 0);
      ctx.lineTo(offset + h, h);
      ctx.stroke();
    }
    ctx.restore();

    // 中央锻炉辉光（卡片行下方的暖光暗示"锻造"）
    const forgeGrad = ctx.createRadialGradient(
      w / 2, h * 0.48, h * 0.05,
      w / 2, h * 0.48, h * 0.5
    );
    forgeGrad.addColorStop(0, "rgba(0, 228, 255, 0.06)");
    forgeGrad.addColorStop(0.3, "rgba(248, 183, 0, 0.03)");
    forgeGrad.addColorStop(1, "rgba(15, 25, 35, 0)");
    ctx.fillStyle = forgeGrad;
    ctx.fillRect(0, 0, w, h);

    // 台座平台线（卡片下方统一底座）
    const platformY = h * 0.55;
    ctx.strokeStyle = "rgba(0, 228, 255, 0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.15, platformY);
    ctx.lineTo(w * 0.85, platformY);
    ctx.stroke();

    // 台座中线微弱的 45° 短线
    ctx.strokeStyle = "rgba(0, 228, 255, 0.03)";
    ctx.lineWidth = 0.5;
    for (let x = w * 0.2; x < w * 0.85; x += 60) {
      ctx.beginPath();
      ctx.moveTo(x, platformY);
      ctx.lineTo(x + 8, platformY + 8);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, platformY);
      ctx.lineTo(x - 8, platformY + 8);
      ctx.stroke();
    }

    // vignette（边缘渐暗）
    const vignette = ctx.createRadialGradient(
      w / 2, h / 2, Math.min(w, h) * 0.35,
      w / 2, h / 2, Math.max(w, h) * 0.75
    );
    vignette.addColorStop(0, "rgba(15, 25, 35, 0)");
    vignette.addColorStop(1, "rgba(5, 10, 15, 0.7)");
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);
  }

  function resizeAndDraw() {
    if (!running) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(ctx, canvas.clientWidth, canvas.clientHeight);
    }
  }

  const ro = new ResizeObserver(() => resizeAndDraw());
  ro.observe(canvas);

  // 初始绘制
  resizeAndDraw();

  return () => {
    running = false;
    cancelAnimationFrame(rafId);
    ro.disconnect();
  };
}

// ============================================================
// 卡片插画路径（PNG 线稿，Ardot 战术美学风格）
// ============================================================

function getCardIllustration(key: string): string {
  const illustrations: Record<string, string> = {
    forge: "/images/armory/forge-anvil.png",
    sharpen: "/images/armory/sharpen-blade.png",
    bear: "/images/armory/bear-shield.png",
    sandbox: "/images/armory/sandbox-map.png",
  };
  return illustrations[key] || "";
}
